import { ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import { LockSimpleIcon, PencilSimpleIcon, ToothIcon } from '@phosphor-icons/react'
import { startCase } from 'lodash'
import { observer } from 'mobx-react-lite'
import type { ReactElement } from 'react'
import { PROCEDURE_FILTER_COLUMNS } from 'app/components/action-center/procedureFilter'
import { NEXT_VISIT_RULE_COLUMN, SEEN_BEFORE_RULE_COLUMN } from 'lib/filterRuleColumns'
import { TreatmentTable } from 'app/components/action-center/TreatmentTable'
import { ContactDrawer } from 'app/components/contacts/ContactDrawer'
import { PageScroll } from 'app/components/layout/PageScroll'
import { PageHeader } from 'app/components/PageHeader'
import { Search } from 'app/components/Search'
import { Button } from 'app/components/ui/button'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import { TableFiltersView } from 'app/components/ui/table-filters/table-filters'
import { useDocumentTitle } from 'app/hooks/useDocumentTitle'
import { usePmsIntegration } from 'app/hooks/usePmsIntegration'
import { useTreatmentPlansPage } from './useTreatmentPlansPage'

const TITLE = `Treatment Plans`
const FILTER_COLUMNS = [NEXT_VISIT_RULE_COLUMN, SEEN_BEFORE_RULE_COLUMN, ...PROCEDURE_FILTER_COLUMNS]

// observer: its saved list waits on a feature toggle, which resolves async.
const TreatmentPlans = observer(function TreatmentPlans(): ReactElement {
  const page = useTreatmentPlansPage()
  const { board } = page
  const rowCount = board.rows.length
  // A saved list reads under its own name, as it does in the sidebar.
  const title = page.list ? startCase(page.list.name) : TITLE
  useDocumentTitle(title)
  return (
    <PageScroll
      variant="table"
      header={
        <PageHeader
          title={title}
          totalItems={page.ready ? board.total : undefined}
          otherButtons={[
            ...(page.list
              ? [
                  <Button key="edit" variant="white" size="sm" onClick={page.list.edit}>
                    <PencilSimpleIcon className="h-4 w-4" />
                    Edit list
                  </Button>,
                ]
              : []),
            <Button key="export" variant="white" size="sm" onClick={page.exportRows} disabled={rowCount === 0}>
              <ArrowDownTrayIcon className="h-4 w-4" />
              {`Export ${rowCount.toLocaleString(`en-US`)} ${rowCount === 1 ? `row` : `rows`}`}
            </Button>,
          ]}
        />
      }
    >
      {board.error && (
        <p role="alert" className="mb-4 text-body-xs text-strong">
          {board.error}
        </p>
      )}
      <div className="flex min-h-0 flex-1 flex-col">
        <TreatmentTable
          rows={board.rows}
          ready={page.ready}
          loading={board.loading}
          hasMore={board.hasMore}
          loadMore={board.loadMore}
          sort={page.sort}
          onSort={page.setSort}
          // One door on a records page: the row and its Open button both open the patient
          // on the Treatment Plans tab. The case dialog stays the Unscheduled Treatment list's.
          onOpenCase={page.openRecord}
          onOpenRecord={page.openRecord}
          setStage={board.setStage}
          stages={board.destinations}
          lockedStage={board.bookedStage}
          banded={false}
          onVisibleColumnsChange={page.setVisibleColumnIds}
          controls={
            <>
              <Search />
              <TableFiltersView columns={FILTER_COLUMNS} />
            </>
          }
        />
      </div>
      {/* Keyed so a second patient opened from this page seeds the tab again. */}
      <ContactDrawer key={page.drawer.contact?.id} {...page.drawer} />
    </PageScroll>
  )
})

// The nav row is hidden when the PMS can't serve this page, but a saved link still lands here.
function UnavailableTreatmentPlans({ locked }: { locked: boolean }): ReactElement {
  useDocumentTitle(TITLE)
  return (
    <PageScroll variant="table" header={<PageHeader title={TITLE} />}>
      {locked ? (
        <TableEmptyState
          icon={<LockSimpleIcon className="h-6 w-6" />}
          title="Treatment Plans needs a PMS integration"
          description="Treatment comes from your practice management system, and none of this workspace's locations has an active PMS integration yet."
        />
      ) : (
        <TableEmptyState
          icon={<ToothIcon className="h-6 w-6" />}
          title="Treatment Plans isn't available for your PMS"
          description="Treatment plans come from Open Dental, Dentrix or Eaglesoft. This workspace's practice management system doesn't share them with Trackable."
        />
      )}
    </PageScroll>
  )
}

export function TreatmentPlansPage(): ReactElement | null {
  const { treatmentGate } = usePmsIntegration()
  if (treatmentGate === undefined) return null
  if (treatmentGate === `available` || treatmentGate === `partial`) return <TreatmentPlans />
  return <UnavailableTreatmentPlans locked={treatmentGate === `padlock`} />
}
